import { Map, OrderedMap } from 'immutable';

import * as Constants from '../constants';

/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map of the tracks loaded into the app
 */
export function getTrackEntitiesFromState(state) {
  return state.entities.get('playlistTracks');
}

/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map of the stations loaded into the app
 */
export function getStationEntities(state) {
  return state.entities && state.entities.get('stations');
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationFactoryId - a station factory id
 * @returns {string} - if the station factory id seeded a station in the station entities Map, that station's id is returned
 */
export function getStationIdByFactoryId(state, stationFactoryId) {
  const stationEntities = getStationEntities(state);
  const station = [...stationEntities].find(currentEntity => {
    const currentEntityFactoryId = currentEntity[1].get('stationFactoryPandoraId');
    return currentEntityFactoryId === stationFactoryId;
  });
  const stationId = station ? station[0] : null;
  return stationId;
}
/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map of the artists loaded into the app
 */
export function getArtistEntities(state) {
  return state.entities && state.entities.get('artists', Map());
}

/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map of the genres loaded into the app
 */
export function getGenreEntities(state) {
  return state.entities && state.entities.get('genres');
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} trackToken - trackToken
 * @returns {Map} - an Immutable Map containing information about the track if found in the store, null otherwise
 */
export function getTrackById(state, trackToken) {
  const tracks = getTrackEntitiesFromState(state);
  return tracks.get(trackToken, null);
}

export function getAllAnnotations(state) {
  return state.entities.getIn(['catalog', 'annotations'], OrderedMap());
}

export function getAnnotationsById(state, id) {
  return state.entities.getIn(['catalog', 'annotations', id], null);
}

/**
 *
 * @param {object} state -
 * @param {List | Array} pandoraIds -
 * @returns {List | Array}
 */
export function getManyAnnotationsById(state, pandoraIds) {
  return pandoraIds.map(pandoraId => {
    return getAnnotationsById(state, pandoraId);
  });
}

export function getPandoraIdsWithoutAnnotations(state, pandoraIds) {
  return pandoraIds.filter(id => !getAnnotationsById(state, id));
}

export function getDetails(state, pandoraId) {
  return state.entities.getIn(['catalog', 'details', pandoraId], Map());
}

/*
 * Used by getEntityById
 */
const idPrefixToEntityMap = {
  R: 'artists',
  C: 'artists', // composers are being stored in the artists entity
  L: 'albums',
  G: 'genres',
  S: 'tracks',
  t: 'events', // Ticketfly event id's begin with 'tf'
  s: 'events', // Songkick event id's begin with 'sk'
};

/*
 * Used by getEntityTrackingType
 * Singular version of entity types used in addVariety analytics
 */
const idPrefixToEntityMapSingular = {
  R: 'artist',
  C: 'artist', // composers are being stored in the artists entity
  L: 'album',
  G: 'genre',
  S: 'song',
  t: 'event', // Ticketfly event id's begin with 'tf'
  s: 'event', // Songkick event id's begin with 'sk'
};

/**
 * @param  {string} id - musicId, stationId, or eventId
 * @returns {string} - The entity type
 */

export function getEntityTrackingType(id) {
  const entityId = typeof id === 'string' ? id : id.toString();
  const sourceFirstLetter = entityId.slice(0, 1);
  return idPrefixToEntityMapSingular[sourceFirstLetter];
}

/**
 * @param  {string} id - musicId, stationId, or eventId
 * @returns {string} - The entity type
 */

export function getEntityTypeById(id) {
  if (!id) return null;
  const entityId = typeof id === 'string' ? id : id.toString();
  const sourceFirstLetter = entityId.slice(0, 1);
  return idPrefixToEntityMap[sourceFirstLetter];
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} id - musicId, stationId, or eventId
 * @returns {Map} - The entity associated with the musicId if found, otherwise null
 */
export function getEntityById(state, id) {
  const sourceType = getEntityTypeById(id);
  const entity = state.entities.getIn([sourceType, id]);
  return entity ? entity : null;
}

// Playlist entity

export function willClobberOptimisticTrackEdits(oldVersion, oldOptimisticVersion, newVersion) {
  return !!oldVersion && !!oldOptimisticVersion && newVersion < oldOptimisticVersion;
}

export function getNextOptimisticVersion(state, playlistId) {
  const version = state.getIn([playlistId, 'version'], null);
  const optimisticVersion = state.getIn([playlistId, 'optimisticVersion'], null);
  let result = 1;
  if (version) {
    result = optimisticVersion > version ? optimisticVersion + 1 : version + 1;
  }
  return result;
}

const PandoraTypeToParentIdField = Object.freeze({
  [Constants.SourceTypes.PandoraTypes.EPISODE]: 'podcastId',
  [Constants.SourceTypes.PandoraTypes.SONG]: 'albumId',
});

export function getParentIdFieldByChildType(pandoraType) {
  return PandoraTypeToParentIdField[pandoraType] || null;
}

export function getParentIdByChildId(state, pandoraId) {
  const child = getAnnotationsById(state, pandoraId);
  if (child) {
    const pandoraType = child.get('type');
    const parentIdField = getParentIdFieldByChildType(pandoraType);
    return child.get(parentIdField);
  }
}
